export const GLB_IMPORT_ROUTE = '/jobs';
export const S123_OPTIMIZATION_ROUTE = '/variant-optimization-jobs';

export const TRANSFORMATION_PROFILES = Object.freeze({
  GLB_IMPORT: 'glbImport',
  S123_OPTIMIZATION: 's123Optimization',
});

export const MATERIAL_OPTIONS = Object.freeze([
  Object.freeze({ value: 'preserve' }),
  Object.freeze({ value: 'source' }),
  Object.freeze({ value: 'matcapTextured' }),
  Object.freeze({ value: 'matcapSolidColor' }),
  Object.freeze({ value: 'unlitTextured' }),
  Object.freeze({ value: 'unlitSolidColor' }),
]);

export const SINGLE_SIDED_OPTIONS = Object.freeze([
  Object.freeze({ value: 'safeOnly' }),
  Object.freeze({ value: 'forceAll' }),
  Object.freeze({ value: 'off' }),
]);

export const SMALL_DETAIL_ACTION_OPTIONS = Object.freeze([
  Object.freeze({ value: 'keep' }),
  Object.freeze({ value: 'delete' }),
]);

export const SMALL_DETAIL_THRESHOLD_MODE_OPTIONS = Object.freeze([
  Object.freeze({ value: 'auto' }),
  Object.freeze({ value: 'manual' }),
]);

export const RENDER_OUTPUT_MODE_OPTIONS = Object.freeze([
  Object.freeze({ value: 'sequence' }),
  Object.freeze({ value: 'sphere' }),
]);

const MATERIAL_BY_OPTION = Object.freeze({
  preserve: Object.freeze({
    shadingModel: 'preserve',
    simplification: 'off',
  }),
  source: Object.freeze({
    shadingModel: 'preserve',
    simplification: 'safe',
  }),
  matcapTextured: Object.freeze({
    profile: 'textured',
    preset: 'three-default',
    shadingModel: 'matcap',
  }),
  matcapSolidColor: Object.freeze({
    profile: 'solidColor',
    preset: 'three-default',
    shadingModel: 'matcap',
  }),
  unlitTextured: Object.freeze({
    shadingModel: 'unlit',
    simplification: 'safe',
  }),
  unlitSolidColor: Object.freeze({
    shadingModel: 'unlit',
    simplification: 'solidColor',
  }),
});

const PROFILE_VALUES = Object.freeze(Object.values(TRANSFORMATION_PROFILES));
const MATERIAL_VALUES = Object.freeze(MATERIAL_OPTIONS.map(({ value }) => value));
const SINGLE_SIDED_VALUES = Object.freeze(SINGLE_SIDED_OPTIONS.map(({ value }) => value));
const SMALL_DETAIL_ACTION_VALUES = Object.freeze(
  SMALL_DETAIL_ACTION_OPTIONS.map(({ value }) => value),
);
const SMALL_DETAIL_THRESHOLD_MODE_VALUES = Object.freeze(
  SMALL_DETAIL_THRESHOLD_MODE_OPTIONS.map(({ value }) => value),
);
const RENDER_OUTPUT_MODE_VALUES = Object.freeze(
  RENDER_OUTPUT_MODE_OPTIONS.map(({ value }) => value),
);

export class TransformationSettingsValidationError extends Error {
  constructor(field, message) {
    super(message);
    this.name = 'TransformationSettingsValidationError';
    this.field = field;
  }
}

function validationError(field, expectation) {
  return new TransformationSettingsValidationError(
    field,
    `Transformation setting "${field}" ${expectation}.`,
  );
}

function assertKnownValue(field, value, knownValues) {
  if (!knownValues.includes(value)) {
    throw validationError(field, `must be one of: ${knownValues.join(', ')}`);
  }

  return value;
}

function readBoolean(field, value) {
  if (typeof value !== 'boolean') {
    throw validationError(field, 'must be a boolean');
  }

  return value;
}

function readFiniteNumber(field, value) {
  const numberValue = typeof value === 'string' && value.trim().length > 0
    ? Number(value)
    : value;

  if (typeof numberValue !== 'number' || !Number.isFinite(numberValue)) {
    throw validationError(field, 'must be a finite number');
  }

  return numberValue;
}

function readNumberInRange(field, value, minimum, maximum) {
  const numberValue = readFiniteNumber(field, value);

  if (numberValue < minimum || numberValue > maximum) {
    throw validationError(field, `must be between ${minimum} and ${maximum}`);
  }

  return numberValue;
}

function readPositiveNumber(field, value) {
  const numberValue = readFiniteNumber(field, value);

  if (numberValue <= 0) {
    throw validationError(field, 'must be greater than 0');
  }

  return numberValue;
}

function readTextureDimension(field, value) {
  const numberValue = readFiniteNumber(field, value);

  if (!Number.isInteger(numberValue) || numberValue < 1 || numberValue > 4096) {
    throw validationError(field, 'must be an integer from 1 through 4096');
  }

  return numberValue;
}

function assertProfile(profile) {
  return assertKnownValue('profile', profile, PROFILE_VALUES);
}

export function createTransformationFormDefaults(profile = TRANSFORMATION_PROFILES.GLB_IMPORT) {
  assertProfile(profile);

  return {
    automaticOptimizationsEnabled: true,
    batchingEnabled: false,
    heavyGeometryEnabled: false,
    maxError: 0.01,
    preserveBorders: true,
    targetTrianglesPerSquareMeter: 2000,
    smallDetailAction: 'keep',
    smallDetailThresholdMode: 'auto',
    smallDetailThresholdMeters: 0.1,
    targetMaterial: 'preserve',
    singleSidedMaterials: 'off',
    textureResizeEnabled: false,
    ktx2Enabled: false,
    maxDimension: 1024,
    physicalSizeCapEnabled: true,
    minDimension: 256,
    pixelsPerMeter: 1024,
    renderOutputMode: 'sequence',
  };
}

function buildTargetMaterial(targetMaterial) {
  const materialOption = assertKnownValue(
    'targetMaterial',
    targetMaterial,
    MATERIAL_VALUES,
  );

  return { ...MATERIAL_BY_OPTION[materialOption] };
}

function buildSingleSidedMaterials(singleSidedMaterials) {
  const singleSidedOption = assertKnownValue(
    'singleSidedMaterials',
    singleSidedMaterials,
    SINGLE_SIDED_VALUES,
  );

  return singleSidedOption === 'off' ? false : singleSidedOption;
}

function buildSmallDetailThreshold(values) {
  const thresholdMode = assertKnownValue(
    'smallDetailThresholdMode',
    values.smallDetailThresholdMode,
    SMALL_DETAIL_THRESHOLD_MODE_VALUES,
  );

  return thresholdMode === 'auto'
    ? 'auto'
    : readPositiveNumber('smallDetailThresholdMeters', values.smallDetailThresholdMeters);
}

function buildTextureResizeSettings(values) {
  if (!readBoolean('textureResizeEnabled', values.textureResizeEnabled)) {
    return false;
  }

  const physicalSizeCapEnabled = readBoolean(
    'physicalSizeCapEnabled',
    values.physicalSizeCapEnabled,
  );

  return {
    maxDimension: readTextureDimension('maxDimension', values.maxDimension),
    physicalSizeCap: physicalSizeCapEnabled
      ? {
          minDimension: readTextureDimension('minDimension', values.minDimension),
          pixelsPerMeter: readPositiveNumber('pixelsPerMeter', values.pixelsPerMeter),
        }
      : false,
  };
}

export function buildTransformationSettings({
  profile,
  values = {},
} = {}) {
  const resolvedProfile = assertProfile(profile);

  if (typeof values !== 'object' || values === null || Array.isArray(values)) {
    throw validationError('values', 'must be an object');
  }

  const defaultValues = createTransformationFormDefaults(resolvedProfile);
  const resolvedValues = {
    ...defaultValues,
    ...values,
  };
  const automaticOptimizationsEnabled = readBoolean(
    'automaticOptimizationsEnabled',
    resolvedValues.automaticOptimizationsEnabled,
  );
  const batchingEnabled = readBoolean(
    'batchingEnabled',
    resolvedValues.batchingEnabled,
  );
  const heavyGeometryEnabled = readBoolean(
    'heavyGeometryEnabled',
    resolvedValues.heavyGeometryEnabled,
  );
  const smallDetailAction = assertKnownValue(
    'smallDetailAction',
    resolvedValues.smallDetailAction,
    SMALL_DETAIL_ACTION_VALUES,
  );

  assertKnownValue(
    'renderOutputMode',
    resolvedValues.renderOutputMode,
    RENDER_OUTPUT_MODE_VALUES,
  );

  return {
    actions: {
      smallVisualDetail: smallDetailAction,
    },
    passes: {
      automatic: {
        enabled: automaticOptimizationsEnabled,
      },
      cleanup: {
        applyGenericCleanup: false,
      },
      materials: {
        deduplicate: true,
        forceOpaqueMaterials: false,
        forceSingleSidedMaterials: buildSingleSidedMaterials(
          resolvedValues.singleSidedMaterials,
        ),
        materialWorkflow: 'coreMetalRoughFallback',
        targetMaterial: buildTargetMaterial(resolvedValues.targetMaterial),
      },
      textures: {
        deduplicateDecodedPixels: true,
        deduplicateExactBytes: true,
        resizeTextures: buildTextureResizeSettings(resolvedValues),
      },
      batching: {
        enabled: resolvedProfile === TRANSFORMATION_PROFILES.GLB_IMPORT
          ? false
          : batchingEnabled,
        strategy: 'global',
        cellSizeMeters: 'infinity',
      },
      heavyGeometry: {
        enabled: heavyGeometryEnabled,
        maxError: heavyGeometryEnabled
          ? readNumberInRange('maxError', resolvedValues.maxError, 0, 1)
          : defaultValues.maxError,
        preserveBorders: heavyGeometryEnabled
          ? readBoolean('preserveBorders', resolvedValues.preserveBorders)
          : defaultValues.preserveBorders,
        targetTrianglesPerSquareMeter: heavyGeometryEnabled
          ? readPositiveNumber(
              'targetTrianglesPerSquareMeter',
              resolvedValues.targetTrianglesPerSquareMeter,
            )
          : defaultValues.targetTrianglesPerSquareMeter,
      },
      geometryPreparation: {
        exactIndexing: heavyGeometryEnabled,
      },
      gpuGeometry: {
        gpuGeometry: false,
        referenceGlbInstancing: false,
      },
      ktx2: {
        enabled: readBoolean('ktx2Enabled', resolvedValues.ktx2Enabled),
      },
    },
    output: {
      targets: ['s123'],
      s123GeometryFormat: 'glb',
    },
    traits: {
      smallVisualDetail: {
        threshold: smallDetailAction === 'delete'
          ? buildSmallDetailThreshold(resolvedValues)
          : 'auto',
      },
    },
  };
}
